import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import DatePicker from "react-datepicker";
import { Form, Select } from "semantic-ui-react";
import IconButton from "../../../shared/buttons/IconButton";
import ExternalOrganizationOnlyViewComponent from "./ExternalOrganizationOnlyViewComponent";
import "./ExternalOrganizationUnitComponent.css";

const getDatePickerValue = value => {
  if (!value) return null;

  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const getMinimumStartDate = (orgUnits, startDate) => {
  const cutoffDate = startDate === null || startDate === undefined || startDate === ""
    ? null
    : moment(startDate);
  const lastOrgUnit = orgUnits.length > 0 ? orgUnits[orgUnits.length - 1] : null;
  const lastStartDate = lastOrgUnit && lastOrgUnit.validity_start_date
    ? moment(lastOrgUnit.validity_start_date)
    : null;

  if (
    lastStartDate &&
    lastStartDate.isValid() &&
    cutoffDate &&
    cutoffDate.isValid()
  ) {
    return lastStartDate.isSameOrAfter(cutoffDate)
      ? lastStartDate.clone().add(1, "days").toDate()
      : cutoffDate.toDate();
  }

  if (cutoffDate && cutoffDate.isValid()) return cutoffDate.toDate();
  if (lastStartDate && lastStartDate.isValid()) {
    return lastStartDate.clone().add(1, "days").toDate();
  }

  return undefined;
};

/**
 * Gestisce la selezione delle organization unit in creazione e il cambio di unità in modifica.
 *
 * Props:
 *   selectedOrgUnits     {Array}     Organization unit selezionate, incluso l'eventuale draft
 *   onChangeOrgUnits     {Function}  Aggiorna un campo di una organization unit. Firma: fn(index, value, fieldName)
 *   onAddOrgUnit         {Function}  Aggiunge una nuova organization unit draft
 *   onDeleteOrgUnit      {Function}  Elimina il draft e ripristina la validità della riga precedente
 *   orgUnitOptions       {Array}     Opzioni organization unit indicizzate per riga
 *   companyOptions       {Array}     Opzioni disponibili per la company
 *   editMode             {boolean}   Indica se il form modifica un consulente esistente
 *   onDisableButtonNext  {Function}  Imposta lo stato di abilitazione del pulsante Next
 *   startDate            {Date|string|Object}  [opzionale] Data minima configurata dal supercutoff
 */
const ExternalOrganizationUnitComponent = ({
  selectedOrgUnits,
  onChangeOrgUnits,
  onAddOrgUnit,
  onDeleteOrgUnit,
  orgUnitOptions,
  companyOptions,
  editMode,
  onDisableButtonNext,
  startDate,
}) => {
  const lastIndex = selectedOrgUnits.length - 1;
  const lastOrgUnit = lastIndex >= 0 ? selectedOrgUnits[lastIndex] : null;
  const draftOrgUnit = lastOrgUnit && lastOrgUnit.isDraft ? lastOrgUnit : null;
  const draftIndex = draftOrgUnit ? lastIndex : -1;
  const persistedOrgUnits = draftOrgUnit
    ? selectedOrgUnits.slice(0, draftIndex)
    : selectedOrgUnits;
  const minimumStartDate = getMinimumStartDate(persistedOrgUnits, startDate);

  const handleAddOrgUnitClick = () => {
    onAddOrgUnit();
    onDisableButtonNext(true);
  };

  const handleCancelClick = () => {
    onDeleteOrgUnit();
    onDisableButtonNext(false);
  };

  const handleStartDateChange = newDate => {
    onChangeOrgUnits(draftIndex, newDate, "validity_start_date");
    onDisableButtonNext(
      !draftOrgUnit.company || !draftOrgUnit.org_unit_id || !newDate
    );
  };

  return (
    <>
      {!editMode &&
        selectedOrgUnits.map((unit, unitIndex) => (
          <Form.Group key={unit.org_unit_id || `org-unit-${unitIndex}`}>
            <Form.Field>
              <label>Company</label>
              <Select
                aria-label="Company"
                className="external-org-unit-full-width"
                placeholder="Company"
                search={true}
                options={companyOptions}
                value={unit.company}
                onChange={(event, data) =>
                  onChangeOrgUnits(unitIndex, data.value, "company")
                }
              />
            </Form.Field>

            {unit.company && (
              <Form.Field>
                <label>Organization Unit</label>
                <Select
                  aria-label="Organization Unit"
                  className="external-org-unit-full-width"
                  placeholder="Organization Unit"
                  search={true}
                  options={orgUnitOptions[unitIndex] || []}
                  value={unit.org_unit_id}
                  onChange={(event, data) => {
                    onChangeOrgUnits(unitIndex, data.value, "org_unit_id");
                    onDisableButtonNext(false);
                  }}
                />
              </Form.Field>
            )}
          </Form.Group>
        ))}

      {editMode && (
        <Form.Group>
          <div className="external-org-unit-wrapper">
            <ExternalOrganizationOnlyViewComponent
              selectedOrgUnits={persistedOrgUnits}
            />

            {!draftOrgUnit && (
              <IconButton
                icon="plus"
                onClick={handleAddOrgUnitClick}
                label="Change Organization Unit"
                floated="left"
              />
            )}

            {draftOrgUnit && (
              <>
                <div className="external-org-unit-fields-row">
                  <div className="external-org-unit-fields">
                    <Form.Field className="external-org-unit-field">
                      <label>Company</label>
                      <Select
                        aria-label="Company"
                        className="external-org-unit-full-width"
                        placeholder="Company"
                        search={true}
                        options={companyOptions}
                        value={draftOrgUnit.company || ""}
                        onChange={(event, data) => {
                          onChangeOrgUnits(draftIndex, data.value, "company");
                          onDisableButtonNext(true);
                        }}
                      />
                    </Form.Field>

                    <Form.Field className="external-org-unit-field">
                      <label>Organization Unit</label>
                      <Select
                        aria-label="Organization Unit"
                        className="external-org-unit-full-width"
                        placeholder="Organization Unit"
                        search={true}
                        options={orgUnitOptions[draftIndex] || []}
                        value={draftOrgUnit.org_unit_id || ""}
                        onChange={(event, data) => {
                          onChangeOrgUnits(
                            draftIndex,
                            data.value,
                            "org_unit_id"
                          );
                          onDisableButtonNext(
                            !draftOrgUnit.company ||
                              !data.value ||
                              !draftOrgUnit.validity_start_date
                          );
                        }}
                      />
                    </Form.Field>

                    <Form.Field>
                      <label htmlFor="org-unit-start-date">Start Date</label>
                      <DatePicker
                        id="org-unit-start-date"
                        name="org_unit_start_date"
                        selected={getDatePickerValue(
                          draftOrgUnit.validity_start_date
                        )}
                        onChange={handleStartDateChange}
                        placeholderText="Click to select a start date"
                        dateFormat="yyyy-MM-dd"
                        minDate={minimumStartDate}
                      />
                    </Form.Field>

                    <Form.Field>
                      <label htmlFor="org-unit-end-date">End Date</label>
                      <DatePicker
                        id="org-unit-end-date"
                        name="org_unit_end_date"
                        selected={getDatePickerValue(
                          draftOrgUnit.validity_end_date
                        )}
                        dateFormat="yyyy-MM-dd"
                        readOnly={true}
                      />
                    </Form.Field>
                  </div>
                </div>

                <IconButton
                  icon="cancel"
                  color="red"
                  onClick={handleCancelClick}
                  label="Cancel"
                />
              </>
            )}
          </div>
        </Form.Group>
      )}
    </>
  );
};

const dateValuePropType = PropTypes.oneOfType([
  PropTypes.instanceOf(Date),
  PropTypes.string,
  PropTypes.object,
]);

ExternalOrganizationUnitComponent.propTypes = {
  selectedOrgUnits: PropTypes.arrayOf(
    PropTypes.shape({
      company: PropTypes.string,
      org_unit_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      org_unit_code: PropTypes.string,
      validity_start_date: dateValuePropType,
      validity_end_date: dateValuePropType,
      isDraft: PropTypes.bool,
    })
  ).isRequired,
  onChangeOrgUnits: PropTypes.func.isRequired,
  onAddOrgUnit: PropTypes.func.isRequired,
  onDeleteOrgUnit: PropTypes.func.isRequired,
  orgUnitOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
  companyOptions: PropTypes.array.isRequired,
  editMode: PropTypes.bool.isRequired,
  onDisableButtonNext: PropTypes.func.isRequired,
  startDate: dateValuePropType,
};

export { getMinimumStartDate };
export default ExternalOrganizationUnitComponent;
